﻿import { MarketplaceResultType } from "config";
import { Hit } from "_shared/_api/search/search-results";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import {
  isInExcludedLegalEntity,
  useRowChoiceExcludedLegalEntity,
} from "_shared/_views/tables/row-choice-excluded-legal-entity";
import Guid from "_shared/guids/guid";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import CompanyResult from "marketplace/_views/company-result";
import { getMarketplaceProfilePath } from "marketplace/_views/marketplace-profile-link";
import IndividualResult from "marketplace/_views/individual-result";
import TeamResult from "marketplace/_views/team-result";
import MarketplaceCompanyInfoSchema from "marketplace/_schema/marketplace-company-info-schema";
import Individual from "marketplace/individuals/individual";
import MarketplaceIndividualInfoSchema from "marketplace/_schema/marketplace-individual-info-schema";
import { getText } from "marketplace/_views/result-text";
import React from "react";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import Team from "marketplace/teams/team";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";

type ResultsListProps = {
  results: Hit<MarketplaceSearchInfoJSON>[];
  embedded?: boolean;
  selectingTeamLeader?: boolean;
  selectingTeamMember?: boolean;
  selectingTeam?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  compactLayout?: boolean;
  navigate?: NavigateFunction;
  searchParams?: URLSearchParams;
  onTeamMemberSelected?: (teamMember: Individual) => void;
  onTeamSelected?: (team: Team) => void;
};

export default function ResultsList(props: Readonly<ResultsListProps>) {
  const {
    results,
    embedded,
    selectingTeamLeader,
    selectingTeamMember,
    selectingTeam,
    selectedTeamMemberUserIds,
    compactLayout,
    navigate,
    searchParams,
    onTeamMemberSelected,
    onTeamSelected,
  } = props;

  const { openDialog } = useDialog();
  const excludedLegalEntityId = useRowChoiceExcludedLegalEntity();
  const session = useSession();

  function handleResultClicked(
    _event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth = false
  ) {
    if (!embedded) {
      // Read from the address bar rather than from this render's props. A search updates the URL
      // before React has committed the render that carries the new parameters, so a result clicked
      // in that window handed the profile the *previous* search - usually none - and closing the
      // profile dropped the searcher back into an unfiltered marketplace.
      const queryString =
        new URLSearchParams(globalThis.location.search).toString() || searchParams?.toString();
      const profileUrl = getMarketplaceProfilePath(profileType, profileId.value);
      navigate?.(queryString ? `${profileUrl}?${queryString}` : profileUrl);
      return;
    }
    openDialog({
      component: component,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: fullWidth,
      },
    });
  }

  return (
    <React.Fragment>
      {results?.map((hit) => {
        if (
          hit.data?.type === MarketplaceResultType.Company &&
          !selectingTeam &&
          !selectingTeamLeader &&
          !selectingTeamMember
        ) {
          const companyResult = MarketplaceCompanyInfoSchema.validateJSON(hit.data);
          return (
            <CompanyResult
              key={companyResult.id}
              result={companyResult}
              handleResultClicked={handleResultClicked}
              getText={getText}
            />
          );
        }

        if (hit.data?.type === MarketplaceResultType.Team && !selectingTeamLeader && !selectingTeamMember) {
          const teamResult = Object.assign(new MarketplaceTeamInfoAPIResponse(), hit.data); // TODO: Is this necessary?
          return (
            <TeamResult
              key={teamResult.id}
              embedded={embedded ?? false}
              result={teamResult}
              handleResultClicked={handleResultClicked}
              getText={getText}
              session={session}
              navigate={navigate}
              compactLayout={compactLayout}
              onTeamSelected={onTeamSelected}
            />
          );
        }

        if (hit.data?.type === MarketplaceResultType.Individual && !selectingTeam) {
          const individualResult = MarketplaceIndividualInfoSchema.validateJSON(hit.data);
          // A picker may leave out a legal entity's people altogether (#6091).
          if (isInExcludedLegalEntity(excludedLegalEntityId, individualResult.legalEntityId)) return null;
          return (
            <IndividualResult
              selectingAsLeader={selectingTeamLeader}
              selectedTeamMemberUserIds={selectedTeamMemberUserIds}
              compactLayout={compactLayout}
              key={individualResult.id}
              embedded={embedded ?? false}
              result={individualResult}
              session={session}
              navigate={navigate}
              handleResultClicked={handleResultClicked}
              getText={getText}
              onTeamMemberSelected={onTeamMemberSelected}
            />
          );
        }

        return null;
      })}
    </React.Fragment>
  );
}
